import React, { useState, useCallback } from 'react';
import './StarRating.css';

/**
 * Reusable Star Rating Component
 * Supports both display and input modes with half-star precision
 *
 * @param {Object} props
 * @param {number} props.rating - Current rating value (0-5, supports decimals)
 * @param {Function} props.onRatingChange - Callback when rating changes (for input mode)
 * @param {boolean} props.readOnly - If true, display only (no interaction)
 * @param {string} props.size - 'small' | 'medium' | 'large'
 * @param {boolean} props.showValue - Show numeric value next to stars
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.allowHalf - Allow half-star ratings (default: true)
 */
const StarRating = ({
  rating = 0,
  onRatingChange,
  readOnly = false,
  size = 'medium',
  showValue = false,
  className = '',
  allowHalf = true,
}) => {
  const [hoverRating, setHoverRating] = useState(0);

  // Calculate the display rating (hover takes priority for interactive mode)
  const displayRating = !readOnly && hoverRating > 0 ? hoverRating : rating;

  /**
   * Get the appropriate star icon class based on the star position and rating
   * @param {number} starIndex - 1-based index of the star (1-5)
   * @param {number} rating - Current rating value
   * @returns {string} - Bootstrap icon class
   */
  const getStarClass = useCallback((starIndex, ratingValue) => {
    const diff = ratingValue - (starIndex - 1);

    if (diff >= 1) {
      // Full star
      return 'bi-star-fill';
    } else if (diff >= 0.5) {
      // Half star
      return 'bi-star-half';
    } else {
      // Empty star
      return 'bi-star';
    }
  }, []);

  /**
   * Handle mouse move over a star to determine half vs full
   */
  const handleMouseMove = useCallback((e, starIndex) => {
    if (readOnly) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const isLeftHalf = x < rect.width / 2;

    if (allowHalf && isLeftHalf) {
      setHoverRating(starIndex - 0.5);
    } else {
      setHoverRating(starIndex);
    }
  }, [readOnly, allowHalf]);

  /**
   * Handle click on a star
   */
  const handleClick = useCallback((e, starIndex) => {
    if (readOnly || !onRatingChange) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const isLeftHalf = x < rect.width / 2;

    let newRating;
    if (allowHalf && isLeftHalf) {
      newRating = starIndex - 0.5;
    } else {
      newRating = starIndex;
    }

    onRatingChange(newRating);
  }, [readOnly, onRatingChange, allowHalf]);

  /**
   * Handle mouse leave
   */
  const handleMouseLeave = useCallback(() => {
    if (!readOnly) {
      setHoverRating(0);
    }
  }, [readOnly]);

  /**
   * Format rating value for display
   */
  const formatRating = (value) => {
    if (Number.isInteger(value)) {
      return `${value}.0`;
    }
    return value.toFixed(1);
  };

  return (
    <div
      className={`star-rating-component star-rating--${size} ${readOnly ? 'read-only' : 'interactive'} ${className}`}
      onMouseLeave={handleMouseLeave}
    >
      <div className="stars-container">
        {[1, 2, 3, 4, 5].map((starIndex) => (
          <span
            key={starIndex}
            className={`star-wrapper ${!readOnly ? 'clickable' : ''}`}
            onMouseMove={(e) => handleMouseMove(e, starIndex)}
            onClick={(e) => handleClick(e, starIndex)}
          >
            <i className={`bi ${getStarClass(starIndex, displayRating)}`}></i>
          </span>
        ))}
      </div>
      {showValue && (
        <span className="rating-value-display">{formatRating(displayRating)}</span>
      )}
    </div>
  );
};

export default StarRating;
